import { first } from 'lodash';

import { HouseholdChatMessage } from '@/modules/household/chat/contexts/householdChat.context';
import {
  MessageResponseAction,
  MessageStatus,
  MessageType,
} from '@/types/schema';
import { getNodes } from '@/utils/graphqlUtils';

/**
 * Message types that solicit a response from the user through in-card
 * actions rather than the composer. While one of these is PENDING the
 * conversation is blocked on the user's answer. A new interactive type adds
 * itself here plus a renderer registration in messageRenderers.
 */
const INTERACTIVE_MESSAGE_TYPES = new Set<MessageType>([
  MessageType.ConfirmationMessage,
  MessageType.FreeFormQuestionMessage,
  MessageType.GroupedQuestionMessage,
  MessageType.MultipleChoiceSingleSelectMessage,
  MessageType.MultipleChoiceMultiSelectMessage,
]);

/** Whether `message` solicits a response through in-card actions. */
export function isInteractiveMessage(message: HouseholdChatMessage): boolean {
  return INTERACTIVE_MESSAGE_TYPES.has(message.type);
}

/**
 * Whether `message` is an interactive message still awaiting the user's
 * response.
 */
export function isPendingInteractiveMessage(
  message: HouseholdChatMessage
): boolean {
  return (
    isInteractiveMessage(message) && message.status === MessageStatus.Pending
  );
}

/** Finds the interactive message awaiting the user's response, if any. */
export function findPendingInteractiveMessage(
  messages: HouseholdChatMessage[]
): HouseholdChatMessage | null {
  return messages.find(isPendingInteractiveMessage) ?? null;
}

/** Returns the single persisted response to an interactive message, if any. */
export function getInteractiveMessageResponse(message: HouseholdChatMessage) {
  return first(getNodes(message.responseFor)) ?? null;
}

/**
 * Resolution line shown once the message leaves PENDING. Only the RESPONDED
 * verb wording differs per type; the expired/canceled wording is shared.
 */
export function getTerminalStatusText(
  message: HouseholdChatMessage,
  respondedVerbs: Record<MessageResponseAction, string>
): string | null {
  if (message.status === MessageStatus.Pending) {
    return null;
  }
  if (message.status === MessageStatus.Expired) {
    return 'This message has expired.';
  }
  if (message.status === MessageStatus.Canceled) {
    return 'This message was canceled.';
  }

  const response = getInteractiveMessageResponse(message);
  if (!response || !response.sender || !response.responseAction) {
    return 'This message has been responded to.';
  }

  switch (message.type) {
    case MessageType.ConfirmationMessage:
    case MessageType.FreeFormQuestionMessage:
    case MessageType.GroupedQuestionMessage:
    case MessageType.MultipleChoiceSingleSelectMessage:
    case MessageType.MultipleChoiceMultiSelectMessage:
      return `${response.sender?.displayName} ${respondedVerbs[response.responseAction]}`;
  }

  return null;
}
